import React from 'react';
import type { Href } from 'expo-router';
import { Platform, Text, useWindowDimensions, View } from 'react-native';

import { fonts, interMedium, interRegular, interSemiBold } from '../../lib/fonts';
import { responsive } from '../../lib/responsive';
import AgentWorkflowMatrix from './AgentWorkflowMatrix';
import {
    agentClients,
    agentRoutes,
    capabilityItems,
    DOCS_BASE,
    faqItems,
    machineEntries,
    paperSources,
    projectRows,
    promptItems,
    systemSteps,
    useCases,
} from './agentsPageContent';
import {
    ActionLink,
    AgentCard,
    MachineCard,
    RouteCard,
    StepCard,
    UseCaseCard,
} from './AgentsPageCards';
import { contentStyles } from './agentsPageContentStyles';
import { styles } from './agentsPageStyles';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { type LocalizedMessage, resolveMessage } from '../../lib/i18n/messages';

const CREATE_FEED_HREF = '/?openChat=true&entrySurface=agents' as Href;
const AGENT_DOCS_HREF = `${DOCS_BASE}/guides/agent-workflows` as Href;

type SectionTone = 'paper' | 'plain' | 'tint' | 'dark';

const toneStyles = {
    paper: styles.sectionPaper,
    plain: styles.sectionPlain,
    tint: styles.sectionTint,
    dark: styles.sectionDark,
};

function webHeadingProps(level: 1 | 2 | 3) {
    return Platform.OS === 'web' ? ({ role: 'heading', 'aria-level': level } as const) : {};
}

export default function AgentsPageBody({ fontsLoaded }: { fontsLoaded: boolean }) {
    const { i18n } = useLingui();
    const { width } = useWindowDimensions();
    const isNarrow = width < 720;
    const isStacked = width < 960;

    return (
        <>
            <View style={[styles.hero, isNarrow && styles.heroNarrow]}>
                <View style={styles.heroAccentLine} />
                <View style={styles.heroAccentBlock} />
                <View style={[styles.rail, isNarrow && styles.railNarrow]}>
                    <View style={styles.heroCopy}>
                        <Text style={[styles.kicker, fontsLoaded && interSemiBold]}><Trans id="agents.agentspagebody.researchFeedsForPeopleAndAgents" comment="UI copy in components/agents/AgentsPageBody.tsx">
                            Research feeds for people and agents
                        </Trans></Text>
                        <Text
                            accessibilityRole="header"
                            style={[
                                fonts.displayLarge,
                                styles.heroTitle,
                                isNarrow && styles.heroTitleNarrow,
                                fontsLoaded && interSemiBold,
                            ]}
                            {...webHeadingProps(1)}
                            {...responsive('hero-title')}
                        ><Trans id="agents.agentspagebody.aiAgentsWelcome" comment="UI copy in components/agents/AgentsPageBody.tsx">
                            AI Agents welcome.<Text style={styles.heroCursor}>▮</Text>
                        </Trans></Text>
                        <Text style={[styles.heroLead, isNarrow && styles.heroLeadNarrow, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.connectAnAgentToAPaperzillaProjectNewPapersAre" comment="UI copy in components/agents/AgentsPageBody.tsx">
                            Connect an agent to a Paperzilla project. New papers are monitored and ranked against your research question, so every run starts with an up-to-date feed.
                        </Trans></Text>
                        <Text style={[styles.agentLine, fontsLoaded && interSemiBold]}><Trans id="agents.agentspagebody.claudeCodexOpenclawCursorYourAgent" comment="UI copy in components/agents/AgentsPageBody.tsx">
                            Claude · Codex · OpenClaw · Cursor · your agent
                        </Trans></Text>
                        <View style={[styles.heroActions, isNarrow && styles.heroActionsNarrow]}>
                            <ActionLink
                                fontsLoaded={fontsLoaded}
                                href={'/research-briefs' as Href}
                                label={msg`Get a daily or weekly research brief →`}
                                narrow={isNarrow}
                            />
                            <ActionLink
                                fontsLoaded={fontsLoaded}
                                href={CREATE_FEED_HREF}
                                label={msg({ id: "agents.agentspagebody.createAResearchFeed", message: "Create a research feed", comment: "UI label in components/agents/AgentsPageBody.tsx" })}
                                narrow={isNarrow}
                                primary
                            />
                            <ActionLink
                                external
                                fontsLoaded={fontsLoaded}
                                href={AGENT_DOCS_HREF}
                                label={msg({ id: "agents.agentspagebody.readAgentSetupDocs", message: "Read agent setup docs ↗", comment: "UI label in components/agents/AgentsPageBody.tsx" })}
                                narrow={isNarrow}
                            />
                        </View>
                        <Text style={[styles.heroFootnote, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.useTheSameProjectInTheAppByEmailThrough" comment="UI copy in components/agents/AgentsPageBody.tsx">
                            Use the same project in the app, by email, through MCP or the CLI, and as an RSS/Atom feed.
                        </Trans></Text>
                    </View>
                </View>
            </View>

            <PageSection isNarrow={isNarrow} tone="plain">
                <View style={[styles.split, isStacked && styles.splitStacked]}>
                    <View style={styles.splitCopy}>
                        <SectionHeading
                            eyebrow={msg({ id: "agents.agentspagebody.whyAResearchFeed", message: "Why a research feed", comment: "UI eyebrow in components/agents/AgentsPageBody.tsx" })}
                            fontsLoaded={fontsLoaded}
                            isNarrow={isNarrow}
                            title={msg({ id: "agents.agentspagebody.aPaperzillaProjectKeepsYourTopicFiltersAndFeedBetweenSessions", message: "A Paperzilla project keeps your topic, filters, and feed between sessions.", comment: "UI title in components/agents/AgentsPageBody.tsx" })}
                        />
                        <Text style={[styles.bodyText, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.theProjectStoresTheTopicCategoriesIncludeAndExcludeKeywords" comment="UI copy in components/agents/AgentsPageBody.tsx">
                            The project stores the topic, categories, include and exclude keywords, plus the matching papers found over time.
                        </Trans></Text>
                        <Text style={[styles.bodyText, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.paperzillaHandlesMonitoringAndRelevanceRankingYourAgentCanRead" comment="UI copy in components/agents/AgentsPageBody.tsx">
                            Paperzilla handles monitoring and relevance ranking. Your agent can read the feed, compare papers, prepare briefs, and send them where they are needed.
                        </Trans></Text>
                    </View>
                    <View style={styles.splitAside}>
                        <View style={styles.projectCard}>
                            <View style={styles.projectCardHeader}>
                                <View style={styles.projectDot} />
                                <Text style={[styles.projectCardLabel, fontsLoaded && interSemiBold]}><Trans id="agents.agentspagebody.examplePaperzillaProject" comment="UI copy in components/agents/AgentsPageBody.tsx">
                                    Example Paperzilla project
                                </Trans></Text>
                            </View>
                            <View style={styles.projectRows}>
                                {projectRows.map(([key, value], index) => (
                                    <View
                                        key={key}
                                        style={[styles.projectRow, index === projectRows.length - 1 && styles.projectRowLast]}
                                    >
                                        <Text style={[styles.projectKey, fontsLoaded && interSemiBold]}>{key}</Text>
                                        <Text style={[styles.projectValue, fontsLoaded && interRegular]}>{value}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    </View>
                </View>
            </PageSection>

            <PageSection isNarrow={isNarrow} tone="tint">
                <View style={styles.introBlock}>
                    <SectionHeading
                        eyebrow={msg({ id: "agents.agentspagebody.monitoringLoop", message: "Monitoring loop", comment: "UI eyebrow in components/agents/AgentsPageBody.tsx" })}
                        fontsLoaded={fontsLoaded}
                        isNarrow={isNarrow}
                        title={msg({ id: "agents.agentspagebody.howAProjectStaysCurrent", message: "How a project stays current.", comment: "UI title in components/agents/AgentsPageBody.tsx" })}
                    />
                    <Text style={[styles.sectionLead, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.paperzillaChecksTheSourcesBelowAndUpdatesTheRankedProject" comment="UI copy in components/agents/AgentsPageBody.tsx">
                        Paperzilla checks the sources below and updates the ranked project feed as new papers appear.
                    </Trans></Text>
                </View>
                <View style={[styles.stepsGrid, isStacked && styles.gridStacked]}>
                    {systemSteps.map((step) => (
                        <StepCard key={step.number} {...step} fontsLoaded={fontsLoaded} />
                    ))}
                </View>
                <View style={styles.sourceStrip}>
                    <Text style={[styles.sourceLabel, fontsLoaded && interSemiBold]}><Trans id="agents.agentspagebody.monitoredSources" comment="UI copy in components/agents/AgentsPageBody.tsx">Monitored sources</Trans></Text>
                    {paperSources.map((source, index) => (
                        <React.Fragment key={source}>
                            <Text style={[styles.sourceName, fontsLoaded && interSemiBold]}>{source}</Text>
                            {index < paperSources.length - 1 && (
                                <Text style={[styles.sourceDivider, fontsLoaded && interRegular]}>/</Text>
                            )}
                        </React.Fragment>
                    ))}
                </View>
            </PageSection>

            <PageSection isNarrow={isNarrow} tone="plain">
                <SectionHeading
                    eyebrow={msg({ id: "agents.agentspagebody.agentCapabilities", message: "Agent capabilities", comment: "UI eyebrow in components/agents/AgentsPageBody.tsx" })}
                    fontsLoaded={fontsLoaded}
                    isNarrow={isNarrow}
                    title={msg({ id: "agents.agentspagebody.whatAnAgentCanDoWithAPaperzillaProject", message: "What an agent can do with a Paperzilla project.", comment: "UI title in components/agents/AgentsPageBody.tsx" })}
                />
                <Text style={[styles.sectionLead, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.throughMcpOrTheCliAnAgentCanSearchThe" comment="UI copy in components/agents/AgentsPageBody.tsx">
                    Through MCP or the CLI, an agent can search the full feed and retrieve paper metadata or Markdown.
                </Trans></Text>
                <View style={styles.capabilityList}>
                    {capabilityItems.map((item, index) => (
                        <View key={String(item.title.id)} style={[styles.capabilityRow, isNarrow && styles.capabilityRowStacked]}>
                            <Text style={[styles.capabilityNumber, fontsLoaded && interMedium]}>
                                {String(index + 1).padStart(2, '0')}
                            </Text>
                            <Text style={[styles.capabilityTitle, fontsLoaded && interSemiBold]}>{i18n._(item.title)}</Text>
                            <Text style={[styles.capabilityBody, fontsLoaded && interRegular]}>{i18n._(item.body)}</Text>
                        </View>
                    ))}
                </View>
                <View style={styles.caveatBar}>
                    <Text style={[styles.caveatText, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.paperzillaMcpIsReadOrientedUseTheCliWhenAnAutomation" comment="UI copy in components/agents/AgentsPageBody.tsx">
                        Paperzilla MCP is read-oriented. Use the CLI when an automation should leave Useful, Very useful, or Not for me feedback on a recommendation.
                    </Trans></Text>
                </View>
            </PageSection>

            <PageSection isNarrow={isNarrow} tone="dark">
                <SectionHeading
                    dark
                    eyebrow={msg({ id: "agents.agentspagebody.connectionOptions", message: "Connection options", comment: "UI eyebrow in components/agents/AgentsPageBody.tsx" })}
                    fontsLoaded={fontsLoaded}
                    isNarrow={isNarrow}
                    title={msg({ id: "agents.agentspagebody.chooseHowTheAgentConnects", message: "Choose how the agent connects.", comment: "UI title in components/agents/AgentsPageBody.tsx" })}
                />
                <Text style={[styles.sectionLead, styles.sectionLeadLight, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.mcpThePzCliRssAtomAndTheAppAllRead" comment="UI copy in components/agents/AgentsPageBody.tsx">
                    MCP, the pz CLI, RSS/Atom, and the app all read from the same Paperzilla project.
                </Trans></Text>
                <View style={contentStyles.routesGrid}>
                    {agentRoutes.map((route) => (
                        <RouteCard key={String(typeof route.title === 'string' ? route.title : route.title.id)} {...route} fontsLoaded={fontsLoaded} narrow={isNarrow} />
                    ))}
                </View>
            </PageSection>

            <PageSection isNarrow={isNarrow} tone="paper">
                <SectionHeading
                    eyebrow={msg({ id: "agents.agentspagebody.documentedSetups", message: "Documented setups", comment: "UI eyebrow in components/agents/AgentsPageBody.tsx" })}
                    fontsLoaded={fontsLoaded}
                    isNarrow={isNarrow}
                    title={msg({ id: "agents.agentspagebody.connectClaudeCodexOpenclawOrAnotherAgent", message: "Connect Claude, Codex, OpenClaw, or another agent.", comment: "UI title in components/agents/AgentsPageBody.tsx" })}
                />
                <Text style={[styles.sectionLead, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.useMcpForNativeToolCallsOrThePzCli" comment="UI copy in components/agents/AgentsPageBody.tsx">
                    Use MCP for native tool calls or the pz CLI for shell-based workflows.
                </Trans></Text>
                <View style={contentStyles.agentCards}>
                    {agentClients.map((client) => (
                        <AgentCard key={String(typeof client.title === 'string' ? client.title : client.title.id)} {...client} fontsLoaded={fontsLoaded} narrow={isNarrow} />
                    ))}
                </View>
            </PageSection>

            <PageSection isNarrow={isNarrow} nativeID="brief-workflows" tone="plain">
                <SectionHeading
                    eyebrow={msg({ id: "agents.agentspagebody.channelWorkflows", message: "Channel workflows", comment: "UI eyebrow in components/agents/AgentsPageBody.tsx" })}
                    fontsLoaded={fontsLoaded}
                    isNarrow={isNarrow}
                    title={msg({ id: "agents.agentspagebody.sendResearchBriefsToTelegramSlackOrTeams", message: "Send research briefs to Telegram, Slack, or Teams.", comment: "UI title in components/agents/AgentsPageBody.tsx" })}
                />
                <Text style={[styles.sectionLead, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.paperzillaProvidesTheProjectFeedAndRelevanceRankingOpenclawClaude" comment="UI copy in components/agents/AgentsPageBody.tsx">
                    Paperzilla provides the project feed and relevance ranking. OpenClaw, Claude, or Codex can turn the results into a brief and pass it to a channel connector.
                </Trans></Text>
                <Text style={[contentStyles.matrixNote, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.startByReviewingDraftsDirectPostingNeedsAConfiguredConnector" comment="UI copy in components/agents/AgentsPageBody.tsx">
                    Start by reviewing drafts. Direct posting needs a configured connector with permission to post in the channel.
                </Trans></Text>
                <AgentWorkflowMatrix fontsLoaded={fontsLoaded} stacked={isStacked} />
            </PageSection>

            <PageSection isNarrow={isNarrow} tone="paper">
                <SectionHeading
                    eyebrow={msg({ id: "agents.agentspagebody.useCases", message: "Use cases", comment: "UI eyebrow in components/agents/AgentsPageBody.tsx" })}
                    fontsLoaded={fontsLoaded}
                    isNarrow={isNarrow}
                    title={msg({ id: "agents.agentspagebody.commonResearchWorkflows", message: "Common research workflows.", comment: "UI title in components/agents/AgentsPageBody.tsx" })}
                />
                <View style={contentStyles.useCaseGrid}>
                    {useCases.map((useCase, index) => (
                        <UseCaseCard
                            key={String(useCase.title.id)}
                            {...useCase}
                            fontsLoaded={fontsLoaded}
                            index={index + 1}
                            narrow={isNarrow}
                        />
                    ))}
                </View>
            </PageSection>

            <PageSection isNarrow={isNarrow} tone="dark">
                <SectionHeading
                    dark
                    eyebrow={msg({ id: "agents.agentspagebody.tryThesePrompts", message: "Try these prompts", comment: "UI eyebrow in components/agents/AgentsPageBody.tsx" })}
                    fontsLoaded={fontsLoaded}
                    isNarrow={isNarrow}
                    title={msg({ id: "agents.agentspagebody.promptsForYourPaperzillaProject", message: "Prompts for your Paperzilla project.", comment: "UI title in components/agents/AgentsPageBody.tsx" })}
                />
                <Text style={[styles.sectionLead, styles.sectionLeadLight, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.useTheProjectNameAndExplainWhatTheAnswerWill" comment="UI copy in components/agents/AgentsPageBody.tsx">
                    Use the project name and explain what the answer will be used for.
                </Trans></Text>
                <View style={contentStyles.promptsGrid}>
                    {promptItems.map((item) => (
                        <View key={String(item.label.id)} style={[contentStyles.promptCard, isNarrow && contentStyles.promptCardStacked]}>
                            <Text style={[contentStyles.promptLabel, fontsLoaded && interSemiBold]}>{i18n._(item.label)}</Text>
                            <Text style={[contentStyles.promptText, fontsLoaded && interRegular]}>“{i18n._(item.prompt)}”</Text>
                        </View>
                    ))}
                </View>
            </PageSection>

            <PageSection isNarrow={isNarrow} tone="tint">
                <SectionHeading
                    eyebrow={msg({ id: "agents.agentspagebody.forAgentsReadingThis", message: "For agents reading this", comment: "UI eyebrow in components/agents/AgentsPageBody.tsx" })}
                    fontsLoaded={fontsLoaded}
                    isNarrow={isNarrow}
                    title={msg({ id: "agents.agentspagebody.documentationAnAgentCanReadDirectly", message: "Documentation an agent can read directly.", comment: "UI title in components/agents/AgentsPageBody.tsx" })}
                />
                <Text style={[styles.sectionLead, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.everyDocsPageIsAvailableAsMarkdownTheSiteAlso" comment="UI copy in components/agents/AgentsPageBody.tsx">
                    Every docs page is available as Markdown. The site also publishes LLM indexes and a Streamable HTTP MCP endpoint.
                </Trans></Text>
                <View style={[contentStyles.machineGrid, isStacked && styles.gridStacked]}>
                    {machineEntries.map((entry) => (
                        <MachineCard key={String(typeof entry.title === 'string' ? entry.title : entry.title.id)} {...entry} fontsLoaded={fontsLoaded} />
                    ))}
                </View>
            </PageSection>

            <PageSection isNarrow={isNarrow} nativeID="faq" tone="plain">
                <SectionHeading
                    eyebrow={msg({ id: "agents.agentspagebody.faq", message: "FAQ", comment: "UI eyebrow in components/agents/AgentsPageBody.tsx" })}
                    fontsLoaded={fontsLoaded}
                    isNarrow={isNarrow}
                    title={msg({ id: "agents.agentspagebody.commonQuestions", message: "Common questions.", comment: "UI title in components/agents/AgentsPageBody.tsx" })}
                />
                <View style={contentStyles.faqList}>
                    {faqItems.map((item) => (
                        <View key={String(item.question.id)} style={contentStyles.faqItem}>
                            <Text
                                accessibilityRole="header"
                                style={[contentStyles.faqQuestion, fontsLoaded && interSemiBold]}
                                {...webHeadingProps(3)}
                            >
                                {i18n._(item.question)}
                            </Text>
                            <Text style={[contentStyles.faqAnswer, fontsLoaded && interRegular]}>{i18n._(item.answer)}</Text>
                        </View>
                    ))}
                </View>
            </PageSection>

            <View style={[contentStyles.finalCta, isNarrow && contentStyles.finalCtaNarrow]}>
                <View style={[styles.rail, isNarrow && styles.railNarrow]}>
                    <View style={contentStyles.finalCtaCopy}>
                        <Text
                            accessibilityRole="header"
                            style={[
                                contentStyles.finalCtaTitle,
                                isNarrow && contentStyles.finalCtaTitleNarrow,
                                fontsLoaded && interSemiBold,
                            ]}
                            {...webHeadingProps(2)}
                        ><Trans id="agents.agentspagebody.connectAnAgentToAPaperzillaProject" comment="UI copy in components/agents/AgentsPageBody.tsx">
                            Connect an agent to a Paperzilla project.
                        </Trans></Text>
                        <Text style={[contentStyles.finalCtaBody, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.createOneFocusedPaperzillaProjectThenConnectItToThe" comment="UI copy in components/agents/AgentsPageBody.tsx">
                            Create one focused Paperzilla project, then connect it to the agent and channel your team already uses.
                        </Trans></Text>
                        <View style={[styles.heroActions, isNarrow && styles.heroActionsNarrow]}>
                            <ActionLink
                                fontsLoaded={fontsLoaded}
                                href={CREATE_FEED_HREF}
                                label={msg({ id: "agents.agentspagebody.createAResearchFeed2", message: "Create a research feed", comment: "UI label in components/agents/AgentsPageBody.tsx" })}
                                light
                                narrow={isNarrow}
                            />
                            <ActionLink
                                external
                                fontsLoaded={fontsLoaded}
                                href={AGENT_DOCS_HREF}
                                label={msg({ id: "agents.agentspagebody.openAgentDocs", message: "Open agent docs ↗", comment: "UI label in components/agents/AgentsPageBody.tsx" })}
                                lightOutline
                                narrow={isNarrow}
                            />
                        </View>
                        <Text style={[contentStyles.finalCtaNote, fontsLoaded && interRegular]}><Trans id="agents.agentspagebody.manageOauthConnections" comment="UI copy in components/agents/AgentsPageBody.tsx">
                            Review and revoke AI connections in your account. Keep tokenized RSS/Atom URLs private.
                        </Trans></Text>
                    </View>
                </View>
            </View>
        </>
    );
}

export function PageSection({
    children,
    isNarrow,
    nativeID,
    tone,
}: {
    children: React.ReactNode;
    isNarrow: boolean;
    nativeID?: string;
    tone: SectionTone;
}) {
    return (
        <View nativeID={nativeID} style={[styles.section, toneStyles[tone], isNarrow && styles.sectionNarrow]}>
            <View style={[styles.rail, isNarrow && styles.railNarrow]}>{children}</View>
        </View>
    );
}

export function SectionHeading({
    dark = false,
    eyebrow,
    fontsLoaded,
    isNarrow,
    title,
}: {
    dark?: boolean;
    eyebrow: LocalizedMessage;
    fontsLoaded: boolean;
    isNarrow: boolean;
    title: LocalizedMessage;
}) {
    const { i18n } = useLingui();
    return (
        <>
            <Text style={[
                styles.sectionEyebrow,
                dark && styles.sectionEyebrowLight,
                fontsLoaded && interSemiBold,
            ]}>
                {resolveMessage(i18n, eyebrow)}
            </Text>
            <Text
                accessibilityRole="header"
                style={[
                    styles.sectionTitle,
                    isNarrow && styles.sectionTitleNarrow,
                    dark && styles.sectionTitleLight,
                    fontsLoaded && interSemiBold,
                ]}
                {...webHeadingProps(2)}
            >
                {resolveMessage(i18n, title)}
            </Text>
        </>
    );
}
